// AgendaDaySection: A single day's section in the agenda view
// Contains a sticky header (desktop) with the date, mission count, and the list of MissionRow items
// CREATED: Part of the agenda list view for TeamView
// UPDATED: Stronger day header with better visual weight and refined "Aujourd'hui" badge

import { MissionRow } from './MissionRow';
import type { EnrichedMission } from './types';
import type { Service } from '../../../lib/services';
import type { AssignedTaskWithDetails } from '../../../lib/execution';

interface AgendaDaySectionProps {
  date: Date;
  missions: EnrichedMission[];
  services: Service[];
  onMissionClick: (task: AssignedTaskWithDetails) => void;
  periodType: 'week' | 'month';
  isDesktop: boolean;
}

function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function formatDateLabel(date: Date, periodType: 'week' | 'month'): string {
  if (periodType === 'week') {
    const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('fr-FR', { month: 'long' });
    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${day} ${month}`;
  }
  const dayName = date.toLocaleDateString('fr-FR', { weekday: 'short' });
  const day = date.getDate();
  const month = date.toLocaleDateString('fr-FR', { month: 'short' });
  return `${dayName.charAt(0).toUpperCase() + dayName.slice(1).replace('.', '')} ${day} ${month}.`;
}

export function AgendaDaySection({
  date,
  missions,
  services,
  onMissionClick,
  periodType,
  isDesktop,
}: AgendaDaySectionProps) {
  const today = isToday(date);
  const label = formatDateLabel(date, periodType);
  const count = missions.length;

  const stickyClass = isDesktop ? 'sticky top-0 z-10' : '';

  return (
    <section aria-label={label}>
      <div
        className={`flex items-center justify-between px-4 py-2.5 border-b ${stickyClass} ${
          today
            ? 'bg-primary-50 border-primary-100'
            : 'bg-gray-50 border-gray-100'
        }`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`text-[11px] font-bold uppercase tracking-wider ${
              today ? 'text-primary-700' : 'text-gray-500'
            }`}
          >
            {label}
          </span>
          {today && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-500 text-white">
              Aujourd'hui
            </span>
          )}
        </div>
        {count > 0 && (
          <span className={`text-xs font-semibold tabular-nums ${today ? 'text-primary-600' : 'text-gray-400'}`}>
            {count} mission{count > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {count === 0 ? (
        <div className="px-4 py-3 border-b border-gray-50">
          <span className="text-xs text-gray-400 italic">Aucune mission planifiée</span>
        </div>
      ) : (
        <div>
          {missions.map(task => (
            <MissionRow
              key={task.id}
              task={task}
              services={services}
              onClick={() => onMissionClick(task)}
              isDesktop={isDesktop}
            />
          ))}
        </div>
      )}
    </section>
  );
}
